import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Shell } from "@/components/Shell";
import { CASALE, getSections } from "@/data/questionnaire";
import { computeStrategy } from "@/data/strategy";
import { getSubmission, upsertSubmission, loadWeights, formatDate } from "@/lib/storage";
import { EmailButton } from "@/components/EmailButton";
import { HandoffButton } from "@/components/HandoffButton";
import { marketingBlocks } from "@/lib/handoff";

const BLOCKS = [
  { key: "primary", title: "Subito", sub: "Le prime 4–6 settimane", color: "#C85A32" },
  { key: "secondary", title: "Nei primi mesi", sub: "Entro 3 mesi", color: "#B07A12" },
  { key: "tertiary", title: "Più avanti", sub: "Entro 12 mesi", color: "#6E8358" },
];

export default function PlanPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(() => getSubmission(id));
  if (!sub) {
    navigate("/", { replace: true });
    return null;
  }
  const strategy = computeStrategy(sub.answers, loadWeights(), sub.businessType);
  const setNext = (planNextStep) => setSub(upsertSubmission({ ...sub, planNextStep }));
  const planDoc = () => ({
    kind: "plan", title: `${CASALE.name} · piano d'azione`, subtitle: `Preparato per ${sub.respondent} · ${strategy.label}`,
    blocks: [
      ...BLOCKS.map((b) => ({ heading: `${b.title} (${b.sub})`, bullets: strategy[b.key].map((a) => `${a.title}: ${a.plan}`) })),
      ...(sub.planNextStep ? [{ heading: "Prossimo passo concordato", paragraphs: [sub.planNextStep] }] : []),
    ],
  });

  return (
    <Shell wide header={
      <div className="flex gap-3 no-print">
        <button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate(`/risultato/${id}`)} data-testid="plan-back-btn"><ArrowLeft size={16} /> Risultato</button>
        <EmailButton className="btn btn-ghost !min-h-[44px] text-sm" build={planDoc} />
        <HandoffButton chooser className="btn btn-ghost !min-h-[44px] text-sm" build={(focus = "tutto") => (focus === "tutto" ? planDoc() : { ...planDoc(), blocks: marketingBlocks(sub, strategy, getSections(sub.businessType), focus) })} />
        <button className="btn btn-olive !min-h-[44px] text-sm" onClick={() => window.print()} data-testid="plan-print-btn"><Printer size={16} /> Stampa / PDF</button>
      </div>
    }>
      <article className="plan fade-up card p-7 sm:p-10" data-testid="action-plan">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[#E3D5C3] pb-5">
          <div>
            <p className="eyebrow">Piano d'azione</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-[#2C221E] mt-1">{CASALE.name}</h1>
          </div>
          <p className="text-sm text-[#7A6B63] text-right">Preparato per <strong className="text-[#2C221E]">{sub.respondent}</strong><br />{formatDate(sub.completedAt || sub.createdAt)}</p>
        </div>

        <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="eyebrow">La linea</span>
          <p className="font-serif text-2xl sm:text-3xl font-semibold text-[#2D4030]" data-testid="plan-strategy">{strategy.label}</p>
          {strategy.pace && <p className="text-sm text-[#5C4D46]">· {strategy.pace}</p>}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {BLOCKS.map((b) => (
            <div key={b.key} data-testid={`plan-${b.key}`}>
              <div className="border-t-4 pt-3" style={{ borderColor: b.color }}>
                <p className="font-serif text-2xl font-semibold" style={{ color: b.color }}>{b.title}</p>
                <p className="text-xs uppercase tracking-widest font-semibold text-[#8A7970]">{b.sub}</p>
              </div>
              <ol className="grid gap-4 mt-4">
                {strategy[b.key].length === 0 && <li className="text-sm italic text-[#B0A096]">Nulla di urgente in questa fase</li>}
                {strategy[b.key].map((a, i) => (
                  <li key={a.id} className="flex gap-3">
                    <span className="flex-none w-7 h-7 rounded-full text-white grid place-items-center text-sm font-semibold" style={{ background: b.color }}>{i + 1}</span>
                    <div>
                      <p className="font-semibold text-[#2C221E] leading-snug">{a.title}</p>
                      <p className="text-sm text-[#5C4D46] mt-1 leading-snug">{a.plan}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl bg-[#F3ECE1] p-5">
          <p className="eyebrow mb-2">Prossimo passo concordato</p>
          <textarea className="w-full bg-transparent outline-none resize-none text-[#2C221E] text-base leading-relaxed min-h-[56px] print:min-h-0" rows={2}
            placeholder="Es. Sopralluogo in cucina martedì 14, poi scheda costi dei prossimi tre eventi."
            value={sub.planNextStep || ""} onChange={(e) => setNext(e.target.value)} data-testid="plan-next-step-input" />
        </div>

        <div className="grid grid-cols-2 gap-10 mt-12 text-sm text-[#7A6B63]">
          <div><div className="border-b border-[#8A7970] h-10" />Il consulente</div>
          <div><div className="border-b border-[#8A7970] h-10" />I titolari</div>
        </div>
      </article>
    </Shell>
  );
}
